import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import type { RegistrationApprovalPolicyDto } from '@paladin-monorepo/common';

import { AuthFormShell } from '@/components/PageHeader';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Loading } from '@/components/Loading';
import API from '@/lib/api';

const SIGNUP_COMPLETED_KEY = 'signup_completed';

interface ThankYouLocationState {
  signupCompleted?: boolean;
}

const hasSignupAccess = (state: ThankYouLocationState | null): boolean => {
  if (state?.signupCompleted) {
    return true;
  }
  return sessionStorage.getItem(SIGNUP_COMPLETED_KEY) === '1';
};

export default function ThankYouPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ThankYouLocationState | null;
  const [isValidating] = useState(() => !hasSignupAccess(locationState));

  const { data: approvalPolicy } = useQuery({
    queryKey: ['auth', 'registration-approval'],
    queryFn: async () => {
      const { data } = await API.get<RegistrationApprovalPolicyDto>('/auth/registration-approval');
      return data;
    },
    enabled: !isValidating,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isValidating) {
      navigate('/auth/login', { replace: true });
    }
  }, [isValidating, navigate]);

  if (isValidating) {
    return <Loading />;
  }

  const descriptionParts = [t('thankYou.emailDescription')];
  if (approvalPolicy?.required) {
    descriptionParts.push(t('thankYou.approvalDescription'));
  }
  const description = descriptionParts.join(' ');

  return (
    <AuthLayout contentWidth="wide" hideFooter>
      <title>{t('thankYou.title')}</title>

      <AuthFormShell title={t('thankYou.title')} description={description}>
        <div className="flex flex-col items-center">
          <CheckCircle2 className="mb-6 size-16 text-success" />
        </div>
      </AuthFormShell>
    </AuthLayout>
  );
}
